/**
 * EP 价格档位 — read-only surface of the CNY/USDT forex-band price table.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { configAPI } from '../api/config';
import { TopBar } from '../components/ui/TopBar';
import { IconButton } from '../components/ui/IconButton';
import { Card } from '../components/ui/Card';
import { FormRow } from '../components/ui/FormRow';
import { Button } from '../components/ui/Button';

interface TierRow {
  id: number;
  level: number;
  cny_min: string;
  cny_max: string;
  usdt_price: string;
  usdt_amount: string;
  is_active: boolean;
  note: string | null;
  create_time: string;
  update_time: string;
}

interface ListResp {
  total: number;
  rows: TierRow[];
}

export function EpPricing() {
  const { t } = useTranslation();
  const [rateInput, setRateInput] = useState('7.15');
  const [lookupRate, setLookupRate] = useState<string | null>(null);

  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['ep-pricing', 'tiers', 'active'],
    queryFn: () =>
      configAPI.getEpTiers({
        is_active: 'true',
        page: 1,
        limit: 50,
      } as Parameters<typeof configAPI.getEpTiers>[0]) as unknown as Promise<ListResp>,
    refetchInterval: 60_000,
  });

  const {
    data: active,
    isFetching: activeLoading,
    error: activeError,
  } = useQuery({
    queryKey: ['ep-pricing', 'active', lookupRate],
    queryFn: () =>
      configAPI.getActiveEpPricing({
        exchange_rate: lookupRate!,
      }) as unknown as Promise<TierRow>,
    enabled: lookupRate !== null,
    retry: false,
  });

  function onLookup(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = rateInput.trim();
    if (trimmed.length === 0) return;
    setLookupRate(trimmed);
  }

  return (
    <div className="min-h-screen bg-surface-base pb-20">
      <div className="absolute inset-0 -z-10 bg-space opacity-70" />
      <TopBar
        left={
          <Link to="/trading">
            <IconButton aria-label={t('common.backToEpTrading', '返回 EP 交易')}>
              <BackIcon />
            </IconButton>
          </Link>
        }
        title={t('common.epPricingTiers', 'EP 价格档位')}
      />

      <div className="p-4 space-y-4">

        <p className="text-sm text-text-secondary">
          {t(
            'common.epPricingDesc',
            '档位表按 CNY/USDT 汇率带映射每 1 EP 的 USDT 单价和每 EP 交易单位的 USDT 数量；数据由运营维护，支持历史 20 档基线。',
          )}
        </p>

        <Card className="p-4">
          <div className="text-sm font-semibold text-text-primary">
            {t('common.queryByRate', '按汇率查询当前档位')}
          </div>
          <form onSubmit={onLookup} className="mt-3 space-y-3">
            <FormRow label={t('common.exampleRate', '例：7.15')} required>
              <input
                className="input-dark w-full py-3 font-mono text-sm"
                inputMode="decimal"
                placeholder={t('common.exampleRate', '例：7.15')}
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
              />
            </FormRow>
            <Button type="submit" fullWidth>
              {t('common.query', '查询')}
            </Button>
          </form>

          {lookupRate !== null && activeLoading && (
            <p className="mt-3 text-xs text-text-secondary">{t('common.querying', '查询中…')}</p>
          )}

          {lookupRate !== null && !activeLoading && active && (
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-surface-border pt-3 text-xs">
              <div>
                <div className="text-text-secondary">{t('common.level', '级别')}</div>
                <div className="font-mono text-sm font-semibold text-text-primary">{active.level}</div>
              </div>
              <div>
                <div className="text-text-secondary">{t('exchangeCalc.cnyUsdt', 'CNY 区间')}</div>
                <div className="font-mono text-sm font-semibold text-text-primary">
                  [{active.cny_min}, {active.cny_max}]
                </div>
              </div>
              <div>
                <div className="text-text-secondary">{t('common.usdtPricePerEp', 'USDT 单价 / EP')}</div>
                <div className="font-mono text-sm font-semibold text-brand-300">{active.usdt_price}</div>
              </div>
              <div>
                <div className="text-text-secondary">{t('common.usdtUnitAmount', 'USDT 单位量')}</div>
                <div className="font-mono text-sm font-semibold text-text-primary">{active.usdt_amount}</div>
              </div>
            </div>
          )}

          {lookupRate !== null && !activeLoading && activeError && (
            <p className="mt-3 text-xs text-negative">
              {activeError instanceof ApiError
                ? activeError.message
                : t('common.noMatchingTier', '未找到匹配的档位')}
            </p>
          )}
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-text-primary">{t('common.activeTiers', '在用档位')}</h2>
            <span className="text-[10px] text-text-muted">
              {t('common.refreshEveryMinute', '每分钟刷新一次')}
            </span>
          </div>
          {listLoading && <p className="text-xs text-text-secondary">{t('common.loading', '加載中...')}…</p>}
          {!listLoading && (!list || (list.rows ?? []).length === 0) && (
            <p className="text-xs text-text-secondary">{t('common.noActiveTiers', '暂无在用档位')}</p>
          )}
          {!listLoading && list && (list.rows ?? []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-surface-border text-left text-text-secondary">
                    <th className="py-2 pr-3">{t('common.level', '级别')}</th>
                    <th className="py-2 pr-3">{t('exchangeCalc.cnyUsdt', 'CNY 区间')}</th>
                    <th className="py-2 pr-3">{t('common.usdtPrice', 'USDT 单价')}</th>
                    <th className="py-2 pr-3">{t('common.usdtAmount', 'USDT 单位量')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(list.rows ?? []).map((r) => (
                    <tr key={r.id} className="border-b border-surface-border">
                      <td className="py-2 pr-3 font-mono font-semibold text-text-primary">{r.level}</td>
                      <td className="py-2 pr-3 font-mono text-text-secondary">
                        [{r.cny_min}, {r.cny_max}]
                      </td>
                      <td className="py-2 pr-3 font-mono text-brand-300">{r.usdt_price}</td>
                      <td className="py-2 pr-3 font-mono text-text-primary">{r.usdt_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}
